import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HammerIcon, ListIcon, XIcon } from "@phosphor-icons/react";
import { ShareButtons } from "@/components/ShareButtons";

const links = [
  { to: "/", label: "Home" },
  { to: "/request", label: "Request Service" },
  { to: "/track", label: "Track Request" },
  { to: "/agreement", label: "Sample Contract" },
  { to: "/ratings", label: "Ratings" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-sm bg-navy-900 flex items-center justify-center">
            <HammerIcon size={20} weight="fill" className="text-gold-500" />
          </span>
          <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
            MCC Service Corp<span className="text-gold-600">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors hover:text-gold-600 ${
                pathname === l.to ? "text-gold-600" : "text-slate-700"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/request"
            data-testid="nav-cta-request"
            className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors active:scale-95"
          >
            Get a Quote
          </Link>
        </div>

        <button
          className="md:hidden text-slate-900"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? <XIcon size={24} /> : <ListIcon size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-slate-700"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/request"
            onClick={() => setOpen(false)}
            className="rounded-sm bg-gold-600 text-white text-sm font-semibold px-5 py-2.5 text-center"
          >
            Get a Quote
          </Link>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-navy-900 text-slate-300">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <div className="flex items-center gap-2 mb-4">
          <span className="w-9 h-9 rounded-sm bg-gold-600 flex items-center justify-center">
            <HammerIcon size={20} weight="fill" className="text-white" />
          </span>
          <span className="font-heading font-extrabold text-lg text-white">MCC Service Corp.</span>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
          Commercial & residential repair specialists. One call for every fix.
        </p>
      </div>
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-500 mb-4">Company</p>
        <ul className="space-y-2 text-sm">
          <li><Link to="/request" className="hover:text-gold-500 transition-colors">Request Service</Link></li>
          <li><Link to="/track" className="hover:text-gold-500 transition-colors">Track Request</Link></li>
          <li><Link to="/admin/login" className="hover:text-gold-500 transition-colors">Staff Login</Link></li>
          <li><Link to="/portal" className="hover:text-gold-500 transition-colors">Subcontractor Login</Link></li>
        </ul>
      </div>
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-500 mb-4">Contact</p>
        <p className="text-sm text-slate-400">Mon–Sat · 7am – 7pm</p>
        <p className="text-sm text-slate-400">Emergency service available</p>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-500 mt-6 mb-2">Share us</p>
        <ShareButtons
          tone="dark"
          url={typeof window !== "undefined" ? window.location.origin : ""}
          text="Need a repair? MCC Service Corp. handles commercial & residential fixes."
        />
      </div>
    </div>
    <div className="border-t border-navy-800 py-6 text-center text-xs text-slate-500">
      © {new Date().getFullYear()} MCC Service Corp.. All rights reserved.
    </div>
  </footer>
);
